import { useEffect, useState } from 'react';

import { Chip } from '@mui/material';
import { ConfigurationStatus, InvoiceStatus } from '@prisma/client';

import { enumToCamelCase } from 'src/lib/formatters';

import './chip-styles.css';

interface StatusChipProps {
    status: string;
    type: string;
    camelCase?: boolean;
}

export const enum STATUS_CHIP_TYPES {
    USER = 'user',
    PARTNER = 'partner',
    MERCHANT = 'merchant',
    ONLINE_PRICING = 'onlinePricing',
    CANCELLATION_REASONS = 'cancellationReasons',
    SALES_INVOICE = 'salesInvoice',
    MCC = 'mcc',
    PRODUCTS_CATALOGUE = 'productsCatalogue',
    SEPA_DD_MANDATE = 'sepaDDMandate',
    PARTNER_RESOURCE = 'partnerResource',
}

const StatusChip = ({ status, type, camelCase = true }: StatusChipProps) => {
    let color: 'success' | 'secondary' | 'error' | 'warning' | 'info' | 'primary' | 'default';
    const [statusText, setStatusText] = useState('');

    useEffect(() => {
        let text = status;
        if (type === 'user' && status === 'INACTIVE') {
            text = 'Not Activated';
        } else if (camelCase) {
            text = enumToCamelCase(status);
        }
        setStatusText(text);
    }, [status]);

    switch (type) {
        case STATUS_CHIP_TYPES.USER:
            switch (status) {
                case 'ACTIVE':
                    color = 'success';
                    break;
                case 'PENDING':
                    color = 'secondary';
                    break;
                case 'LOCKED':
                    color = 'error';
                    break;
                case 'NOT_ACTIVATED':
                    color = 'secondary';
                    break;
                case 'CANCELLED':
                    color = 'secondary';
                    break;
                case 'SUSPENDED':
                    color = 'error';
                    break;
                default:
                    color = 'success';
                    break;
            }
            break;

        case STATUS_CHIP_TYPES.PARTNER:
            switch (status) {
                case 'ACTIVE':
                    color = 'success';
                    break;
                case 'CANCELLED':
                    color = 'secondary';
                    break;
                case 'SUSPENDED':
                    color = 'error';
                    break;
                default:
                    color = 'success';
                    break;
            }
            break;

        case STATUS_CHIP_TYPES.MERCHANT:
            switch (status) {
                case 'ACTIVE':
                    color = 'success';
                    break;
                case 'INACTIVE':
                    color = 'secondary';
                    break;
                case 'CANCELLED':
                    color = 'secondary';
                    break;
                case 'SUSPENDED':
                    color = 'error';
                    break;
                default:
                    color = 'success';
                    break;
            }
            break;

        case STATUS_CHIP_TYPES.ONLINE_PRICING:
            switch (status) {
                case 'ACTIVE':
                    color = 'success';
                    break;
                case 'INACTIVE':
                    color = 'secondary';
                    break;
                case 'DELETED':
                    color = 'secondary';
                    break;
                case 'DEACTIVATED':
                    color = 'error';
                    break;
                default:
                    color = 'success';
                    break;
            }
            break;

        case STATUS_CHIP_TYPES.CANCELLATION_REASONS:
            switch (status) {
                case 'ACTIVE':
                    color = 'success';
                    break;
                case 'INACTIVE':
                    color = 'error';
                    break;
                default:
                    color = 'success';
                    break;
            }
            break;

        case STATUS_CHIP_TYPES.SALES_INVOICE:
            switch (status) {
                case InvoiceStatus.PAID:
                    color = 'success';
                    break;
                case InvoiceStatus.UNPAID:
                    color = 'error';
                    break;
                case InvoiceStatus.PARTIALLY_PAID:
                    color = 'secondary';
                    break;
                default:
                    color = 'error';
                    break;
            }
            break;

        case STATUS_CHIP_TYPES.MCC:
            switch (status) {
                case 'LOW':
                    color = 'success';
                    break;
                case 'MEDIUM':
                    color = 'warning';
                    break;
                case 'HIGH':
                    color = 'error';
                    break;
                case 'RESTRICTED':
                    color = 'secondary';
                    break;
                case 'PROHIBITED':
                    color = 'info';
                    break;
            }
            break;

        case STATUS_CHIP_TYPES.PRODUCTS_CATALOGUE:
            switch (status) {
                case 'ACTIVE':
                    color = 'success';
                    break;
                case 'DEACTIVATED':
                    color = 'error';
                    break;
                default:
                    color = 'primary';
                    break;
            }
            break;

        case STATUS_CHIP_TYPES.SEPA_DD_MANDATE:
            switch (status) {
                case 'ACTIVE':
                    color = 'success';
                    break;
                case 'DEACTIVATED':
                    color = 'error';
                    break;
                default:
                    color = 'primary';
                    break;
            }
            break;

        case STATUS_CHIP_TYPES.PARTNER_RESOURCE:
            switch (status) {
                case ConfigurationStatus.ACTIVE:
                    color = 'success';
                    break;
                case ConfigurationStatus.INACTIVE:
                    color = 'secondary';
                    break;
                case ConfigurationStatus.SUSPENDED:
                    color = 'error';
                    break;
                default:
                    color = 'success';
                    break;
            }
            break;

        default:
            color = 'success';
            break;
    }

    return (
        <Chip
            label={statusText}
            variant="outlined"
            color={color}
            sx={{
                height: '24px',
            }}
        />
    );
};

export default StatusChip;
